
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import MultilevelCategoriesFetcher from "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesFetchMixin";
import ContractProductsFiltersMixins from "@/mixins/provisioning/contractProductsFiltersMixins";

// Data/Enums
import { DataModules } from "@/store/modules/data";
import { ProductTypes } from "@/data/enums/products";
import { productsFilters } from "@/data/filters/products";
import { productsSorters } from "@/data/sorters/products";
import { Sorters } from "@/data/table";

// Types
import { IProduct, IProductCategory } from "@/models/products";
import {
  ICatalogueProductsProviderData,
  ICatalogueProductListData,
  ICatalogueProductsProviderMethods,
  ICatalogueProductListMethods
} from "@/models/providers/catalogueProducts";
import { ISorter, IFilter, ITableConfig } from "@/models/tables";

@Component
export default class CatalogueProductsProvider extends Mixins(
  FilteringSortingTableStore,
  MultilevelCategoriesFetcher,
  ContractProductsFiltersMixins
) {
  // Props

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data
  public dataModule = DataModules.CATALOGUE;

  public filters: IFilter[] = [];
  public sorters: ISorter[] = [];

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  public isLoading = true;
  public isReloading = false;

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) {
      this.isLoading = false;
      return;
    }

    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      // Check provisioning first
      await this.getOrgProvisioning(this.isReloading);

      // Load and set filters
      await Promise.all(this.getTableFiltersData());
      this.setFiltersAndSorters();

      // Load actual products

      this.apiProps = {
        with: [
          "image",
          "prices",
          "provision_category",
          "provision_blueprint",
          "provision_provider",
          "provision_provider.logos",
          "provision_configuration"
        ].join(","),
        "filter[product_type]": this.productTypes.join(","),
        ...(this.currencyId ? { currency_id: this.currencyId } : {}),
        ...(this.accountId ? { account_id: this.accountId } : {}),
        ...this.manualFilters
      };

      if (this.isBasketContext)
        this.dataPayloadSettings = { isBasketContext: true };

      await this.getData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public getTableFiltersData() {
    const allPromises = [this.getRootCategories(this.isReloading)];

    if (this.hasProvisioning) {
      allPromises.push(this.getFiltersData(this.isReloading));
    }

    return allPromises;
  }

  public setFiltersAndSorters() {
    this.filters = _.concat(
      _.filter(
        this.getProductCategoryFilledFilters(productsFilters(), false),
        filter => {
          if (!this.isCategoryManual) return filter;
          return filter.key !== "category_id";
        }
      ) as IFilter[],
      _.map(this.createProvisionFilters(false), filter => {
        filter.key = filter.key.replace("product.", ""); // Remove "product." because of different data structure than contract product reused code
        return filter;
      })
    );
    this.sorters = productsSorters;
  }

  public refreshProductsListing() {
    this.getData({ ignoreStored: true });
  }

  // check because it can cause some issues
  public async reloadData() {
    try {
      this.isReloading = true;
      await this.cancelRequests();
      await this.refreshProductsListing();
    } finally {
      this.isReloading = false;
    }
  }

  public getProductWithType(productType: ProductTypes) {
    if (productType === ProductTypes.PRODUCT_OPTION)
      return this.$t("_.product_option");

    if (productType === ProductTypes.PRODUCT_ATTRIBUTE)
      return this.$t("_.product_attribute");

    return this.$t("_.product");
  }

  public async openDeleteProductConfirmation(product: IProduct) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: this.getProductWithType(product.product_type)
              .toString()
              .toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            try {
              await this.deleteProduct(product.id);
              this.$emit("refreshTreeView");
              this.$store.dispatch("toast/show", {
                message: this.$t("_.object_successfully_removed", {
                  object: this.getProductWithType(product.product_type)
                })
              });
              this.refreshProductsListing();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
            confirm.close();
          }
        }
      }
    });
  }

  public deleteProduct(productId: IProduct["id"]) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/remove`, {
      id: productId
    });
  }

  public async startProductDuplication(product: IProduct) {
    try {
      await this.duplicateProduct(product);
      this.$emit("refreshTreeView");
      this.refreshProductsListing();
      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_duplicated", {
          object: this.getProductWithType(product.product_type)
        })
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public duplicateProduct(product) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/duplicate`, {
      id: product.id,
      unitId: _.get(product, "unit_id", null)
    });
  }

  public get categoryRouterName() {
    if (this.itemIsOption) return "adminCatalogueOptionCategory";
    if (this.itemIsAttribute) return "adminCatalogueAttributeCategory";

    return "adminCatalogueCategory";
  }

  public async editCategory(categoryId: IProductCategory["id"]) {
    this.$router.push({
      name: this.categoryRouterName,
      params: {
        categoryId
      }
    });
  }

  // Computed

  public get itemIsOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get itemIsAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get itemRouteName() {
    return this.itemIsOption
      ? "adminCatalogueOption"
      : this.itemIsAttribute
      ? "adminCatalogueAttribute"
      : "adminCatalogueProduct";
  }

  public get isCategoryManual() {
    return _.keys(this.manualFilters).includes("filter[category_id]");
  }

  // Guarding

  public get guardFunctionalities() {
    if (this.itemIsOption)
      return ["list_product_options_categories", "list_product_options"];
    if (this.itemIsAttribute)
      return ["list_product_attributes_categories", "list_product_attributes"];
    return ["list_product_categories", "list_products"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("catalogueProductsProviderData")
  public providerData(): ICatalogueProductsProviderData {
    return {
      categories: this.categories,
      configurations: this.configurations,
      productTypes: this.productTypes
    };
  }

  @Provide("catalogueProductListData")
  public catalogueProductListData(): ICatalogueProductListData {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      config: this.config,
      defaults: this.defaults,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      filters: this.filters,
      hasRequestError: false,
      hasProvisioning: this.hasProvisioning,
      isLoading: this.isLoading || this.table.loading,
      isReloading: this.isReloading,
      itemRouteName: this.itemRouteName,
      limit: this.table.limit,
      page: this.table.page,
      sorters: this.sorters,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("catalogueProductsProviderMethods")
  public get providerMethods(): ICatalogueProductsProviderMethods {
    return {
      editCategory: this.editCategory,
      openDeleteProductConfirmation: this.openDeleteProductConfirmation,
      startProductDuplication: this.startProductDuplication
    };
  }

  @Provide("catalogueProductListMethods")
  public get catalogueProductListMethods(): ICatalogueProductListMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: this.reloadData }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $productsData: this.providerData(),
        $productsMethods: this.providerMethods,
        $catalogueProductListData: this.catalogueProductListData(),
        $catalogueProductListMethods: this.catalogueProductListMethods
      })
    );
  }
}
